import { api } from '../lib/client';
import { listDeployments } from './agent';

const DEFAULT_PAGE_SIZE = 200;

export async function fetchRegistrations() {
  const items = [];
  let nextOffset = 0;

  do {
    const { data } = await api.get('/phone-endpoints', {
      params: {
        type: 'phone-registration',
        offset: nextOffset,
        pageSize: DEFAULT_PAGE_SIZE
      }
    });
    items.push(...(data.items || []));
    nextOffset = data.nextOffset != null ? data.nextOffset : null;
  } while (nextOffset != null);

  return items;
}

export async function createRegistration(payload) {
  const body = {
    type: 'phone-registration',
    name: payload.name || undefined,
    registrar: payload.registrar,
    username: payload.username,
    password: payload.password,
    handler: payload.handler || undefined,
    outbound: payload.outbound ?? false,
    options: payload.options || undefined,
  };
  const { data } = await api.post('/phone-endpoints', body);
  return data;
}

export async function updateRegistration(id, payload) {
  const body = {};
  if (payload.name !== undefined) body.name = payload.name;
  if (payload.handler !== undefined) body.handler = payload.handler;
  if (payload.outbound !== undefined) body.outbound = payload.outbound;
  if (payload.options !== undefined) body.options = payload.options;
  if (payload.registrar !== undefined) body.registrar = payload.registrar;
  if (payload.username !== undefined) body.username = payload.username;
  if (payload.password !== undefined) body.password = payload.password;

  const { data } = await api.put(`/phone-endpoints/${encodeURIComponent(id)}`, body);
  return data;
}

export async function fetchRegistration(id) {
  const { data } = await api.get(`/phone-endpoints/${encodeURIComponent(id)}`);
  return data;
}

export async function activateRegistration(id) {
  const { data } = await api.post(`/phone-endpoints/${encodeURIComponent(id)}/activate`);
  return data;
}

export async function disableRegistration(id) {
  const { data } = await api.post(`/phone-endpoints/${encodeURIComponent(id)}/disable`);
  return data;
}

export async function deleteRegistration(id) {
  const { data } = await api.delete(`/phone-endpoints/${encodeURIComponent(id)}`);
  return data;
}

export async function fetchRegistrationDeployments() {
  const { items } = await listDeployments();
  return items || [];
}

/**
 * Index of the SIP exchanges the b2bua node holding this registration has
 * captured — one line per exchange, without the messages themselves, which run
 * to tens of kilobytes each. Fetch those one at a time with
 * fetchRegistrationTraceEntry using the ids returned here.
 */
export async function fetchRegistrationTraceIndex(id, { since } = {}) {
  const { data, headers } = await api.get(`/phone-endpoints/${encodeURIComponent(id)}/trace`, {
    params: since ? { since } : undefined
  });
  return withProvenance(data, headers);
}

/**
 * One captured exchange in full: every message, with its envelope and raw SIP text.
 *
 * A call is served as its customer-facing leg alone. `debug` adds the leg the
 * node placed to the platform, interleaved by timestamp and marked `leg` — the
 * half that says why a call the customer's PBX saw refused actually failed.
 * Omitted rather than sent as 0 when off, so the ordinary request keeps the URL
 * it has always had.
 */
export async function fetchRegistrationTraceEntry(id, transactionId, { format = 'json', debug = false } = {}) {
  const { data, headers } = await api.get(
    `/phone-endpoints/${encodeURIComponent(id)}/trace/${encodeURIComponent(transactionId)}`,
    { params: debug ? { format, debug: 1 } : { format } }
  );
  return withProvenance(data, headers);
}

/**
 * Which node answered and when. The API puts this in the body of an object
 * response and in headers either way, so read the headers as the fallback.
 */
function withProvenance(data, headers) {
  if (!data || Array.isArray(data) || typeof data !== 'object') return data;
  return {
    ...data,
    node: data.node || headers?.['x-regclient-node'] || null,
    fetchedAt: data.fetchedAt || headers?.['x-regclient-fetched-at'] || null
  };
}

/**
 * A trace as a capture file for Wireshark or sngrep. With a transactionId, just
 * that exchange; without one, the whole registration. TLS legs are exported
 * decrypted, which an on-wire capture can never show. `debug` carries the
 * platform leg into the capture, as it does for the transcript.
 */
export async function fetchRegistrationTracePcap(id, transactionId = null, { debug = false } = {}) {
  const path = transactionId
    ? `/phone-endpoints/${encodeURIComponent(id)}/trace/${encodeURIComponent(transactionId)}`
    : `/phone-endpoints/${encodeURIComponent(id)}/trace`;
  const params = debug ? { format: 'pcap', debug: 1 } : { format: 'pcap' };
  try {
    const { data } = await api.get(path, { params, responseType: 'blob' });
    return data;
  } catch (err) {
    throw await decodeBlobError(err);
  }
}

/**
 * A blob responseType applies to failures too, so the JSON error body of a
 * refused pcap arrives as a Blob. Decode it in place, so error handling doesn't
 * have to know which response type produced the failure.
 */
async function decodeBlobError(err) {
  const data = err?.response?.data;
  if (!data || typeof data.text !== 'function') return err;
  try {
    err.response.data = JSON.parse(await data.text());
  } catch {
    err.response.data = {};
  }
  return err;
}
